/*
 * cart-upsell.js — deploy3 (2026-10-14, cart upsell fix C1/C2)
 *
 * What it does (unchanged for the shopper):
 *   snippets/cart-upsell.liquid renders a hidden template #cart-drawer-upsell-wrapper after </cart-drawer>.
 *   Whenever the drawer has items and is not yet wrapped, <cart-drawer-items> is wrapped in
 *   .cart-drawer-items-wrapper and the template's markup is appended after it. If the list has no
 *   <li>, the copied #cart-drawer-upsell block is removed.
 *
 * What changed vs. the previous version:
 *   1. NEVER fetches window.location.href any more. Fresh upsell markup comes from:
 *        a) the 'cart-drawer' section HTML already contained in the cart response that triggered the
 *           re-render (pubsub cartUpdate -> event.cartData.sections['cart-drawer'], sent by /cart/add from
 *           product-form.js and by /cart/change from the drawer's cart-items), or
 *        b) if that is not available, ONE Section Rendering call scoped to the drawer section:
 *           `${location.pathname}?section_id=cart-drawer` (same page context as before -> same markup).
 *   2. One MutationObserver on #CartDrawer, childList only (no attributes): opening/closing the drawer
 *      (class changes) and attribute toggles no longer trigger anything. Debounced (250 ms, as before).
 *      It only acts when the drawer content was re-rendered (the wrapper is gone) and the cart is not
 *      empty; it disconnects itself when <cart-drawer> leaves the DOM.
 *   3. No network and no pubsub subscription when the template is marked data-upsell-active="false"
 *      (no upsell collection set): the drawer is still wrapped exactly as before (the wrapper div carries
 *      the scroll CSS in cart-upsell.css), but only from the markup already on the page. Nothing runs
 *      at all when the template or the drawer is absent.
 *   4. The /cart page observer (which re-downloaded /cart and then threw a ReferenceError calling the
 *      commented-out insertSectionCartPage) is removed. The upsell is drawer-only (as the old commented
 *      code and the settings info "Visible on cart drawer only" say), so nothing visible changes on /cart.
 *   5. window.onChangeKsUpsellVariantSelector kept (inline onchange= in snippets/cart-upsell.liquid).
 *
 * Depends on (optional): subscribe / PUB_SUB_EVENTS from pubsub.js / constants.js. Without them it
 * always uses the scoped section request (b).
 */
(function () {
  if (window.__rfCartUpsell) return;
  window.__rfCartUpsell = true;

  var TEMPLATE_ID = 'cart-drawer-upsell-wrapper';
  var DEBOUNCE_MS = 250;
  var CACHE_TTL_MS = 10000; // section HTML from a cart response is only trusted for a short time

  /** Variant <select> in an upsell card: swap the card image (inline onchange handler). */
  window.onChangeKsUpsellVariantSelector = function (select, event) {
    event.preventDefault();
    var imgSrc = select.options[select.selectedIndex].dataset.variantImg;

    if (imgSrc) {
      var img = select.closest('.cart-drawer-upsell-list-item').querySelector('.cart-drawer-upsell-list-item-left img');
      img.src = imgSrc;
    }
  };

  function template() {
    return document.getElementById(TEMPLATE_ID);
  }

  function upsellActive(el) {
    return Boolean(el) && el.dataset.upsellActive !== 'false';
  }

  /** Copies the template into the drawer next to <cart-drawer-items> (same steps as before). */
  function insertSectionCartDrawer() {
    if (document.querySelector('#CartDrawer .cart-drawer-items-wrapper')) return;

    var cartDrawerItems = document.querySelector('#CartDrawer cart-drawer-items');
    if (!cartDrawerItems) return;

    if (cartDrawerItems.classList.contains('is-empty')) return;

    var upsells = template();
    if (!upsells) return;

    var wrapper = document.createElement('div');
    wrapper.classList.add('cart-drawer-items-wrapper');
    cartDrawerItems.parentNode.insertBefore(wrapper, cartDrawerItems);
    wrapper.appendChild(cartDrawerItems);

    wrapper.insertAdjacentHTML('beforeend', upsells.innerHTML);

    // Template and copy are identical, so "no <li> anywhere" == "no <li> in the copy".
    if (!wrapper.querySelector('.cart-drawer-upsell-list li')) {
      var block = wrapper.querySelector('#cart-drawer-upsell');
      if (block) block.remove();
    }
  }

  /** Replaces the hidden template with the one found in a section HTML string. */
  function replaceTemplateFrom(html) {
    if (!html) return false;
    var fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(TEMPLATE_ID);
    var current = template();
    if (!fresh || !current) return false;
    current.replaceWith(document.importNode(fresh, true));
    return true;
  }

  function needsInsert() {
    if (document.querySelector('#CartDrawer .cart-drawer-items-wrapper')) return false;
    var items = document.querySelector('#CartDrawer cart-drawer-items');
    return Boolean(items) && !items.classList.contains('is-empty');
  }

  // --------------------------------------------------------------- initial render (server markup)
  var initialTemplate = template();
  if (!initialTemplate) return; // snippet absent: nothing to copy
  var active = upsellActive(initialTemplate); // false: no upsell collection -> never fetch

  insertSectionCartDrawer();

  var drawer = document.querySelector('cart-drawer');
  var container = document.getElementById('CartDrawer');
  if (!drawer || !container) return;

  // --------------------------------------------------------------- fresh markup from cart responses
  var cached = null; // { html, at }

  var unsubscribe = null;
  if (active && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var sections = event && event.cartData && event.cartData.sections;
      var html = sections && sections['cart-drawer'];
      cached = html ? { html: html, at: Date.now() } : null;
    });
  }

  function takeCached() {
    var c = cached;
    cached = null;
    return c && Date.now() - c.at < CACHE_TTL_MS ? c.html : null;
  }

  // --------------------------------------------------------------- re-render handling
  var timer = null;
  var inFlight = null; // AbortController of the fallback section request
  var seq = 0;

  function refresh() {
    if (!needsInsert()) return;

    if (!active) {
      // No upsell products configured: the list is empty in every render, so the markup on the page
      // is already current. Only the wrapper is (re)created, as before.
      insertSectionCartDrawer();
      return;
    }

    var html = takeCached();
    if (html) {
      replaceTemplateFrom(html);
      insertSectionCartDrawer();
      return;
    }

    // Fallback: one scoped Section Rendering request (never the full page).
    if (inFlight) inFlight.abort();
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight = controller;
    var mySeq = ++seq;
    var url = window.location.pathname + '?section_id=cart-drawer';

    fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('cart-drawer section ' + response.status);
        return response.text();
      })
      .then(function (text) {
        if (mySeq !== seq || !drawer.isConnected) return;
        replaceTemplateFrom(text);
        insertSectionCartDrawer();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.error(err);
        // Still show the (possibly slightly stale) server-rendered list rather than nothing.
        if (mySeq === seq) insertSectionCartDrawer();
      })
      .then(function () {
        if (inFlight === controller) inFlight = null;
      });
  }

  var observer = new MutationObserver(function () {
    if (!drawer.isConnected) {
      teardown();
      return;
    }
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      refresh();
    }, DEBOUNCE_MS);
  });

  function teardown() {
    observer.disconnect();
    if (timer) clearTimeout(timer);
    if (inFlight) inFlight.abort();
    if (unsubscribe) unsubscribe();
  }

  // childList only: re-renders replace #CartDrawer's / .drawer__inner's children or swap
  // <cart-drawer-items>; class/attribute changes (open/close, pickup toggles) are ignored.
  observer.observe(container, { childList: true, subtree: true });
})();
